import React, { useState } from 'react';
import { TrendingUp, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { TRIGGERS } from '../components/TriggerSelector';

const MOOD_LABELS = {
  excellent: '😊 מצוין',
  good: '🙂 טוב',
  neutral: '😐 ממוצע',
  difficult: '😔 קשה',
  critical: '😰 קריטי',
};

const TRIGGER_LABELS = Object.fromEntries(TRIGGERS.map(t => [t.value, t.label]));

const WeeklyReportScreen = () => {
  const { userData, dailyLogs } = useApp();
  const [weekOffset, setWeekOffset] = useState(0);

  // Get week range
  const getWeekDays = (offset = 0) => {
    const days = [];
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() - offset * 7);
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  };

  const weekDays = getWeekDays(weekOffset);
  const weekStart = weekDays[0].toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
  const weekEnd = weekDays[6].toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });

  const getDayLog = (date) => {
    const key = date.toISOString().split('T')[0];
    return dailyLogs[key] || null;
  };

  const cleanDays = weekDays.filter(d => getDayLog(d)?.isClean).length;
  const loggedDays = weekDays.filter(d => getDayLog(d)).length;
  const successRate = loggedDays > 0 ? Math.round((cleanDays / loggedDays) * 100) : 0;

  // Mood count
  const moodCount = {};
  weekDays.forEach(d => {
    const log = getDayLog(d);
    if (log?.mood) moodCount[log.mood] = (moodCount[log.mood] || 0) + 1;
  });
  const topMood = Object.entries(moodCount).sort((a, b) => b[1] - a[1])[0]?.[0];

  // Trigger count
  const triggerCount = {};
  weekDays.forEach(d => {
    const log = getDayLog(d);
    if (log?.triggers) log.triggers.forEach(t => triggerCount[t] = (triggerCount[t] || 0) + 1);
  });
  const topTrigger = Object.entries(triggerCount).sort((a, b) => b[1] - a[1])[0]?.[0];

  const DAY_NAMES = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

  const handleShare = () => {
    const text = `📊 דוח שבועי - יומן גמילה\n\n🗓️ ${weekStart} - ${weekEnd}\n✅ ימים נקיים: ${cleanDays}/7\n📈 שיעור הצלחה: ${successRate}%\n🔥 רצף נוכחי: ${userData.streak} ימים\n\nנשלח מיומן גמילה 💙`;
    if (navigator.share) {
      navigator.share({ title: 'דוח שבועי', text });
    } else {
      navigator.clipboard?.writeText(text);
      alert('הדוח הועתק!');
    }
  };

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header title="דוח שבועי" subtitle="סיכום התקדמות" />

      <div className="px-4 py-4 space-y-4">
        {/* Week Navigation */}
        <div className="bg-white rounded-xl p-4 shadow">
          <div className="flex justify-between items-center mb-4">
            <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 hover:bg-stone-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
            <h2 className="font-bold text-stone-800">{weekStart} — {weekEnd}</h2>
            <button
              onClick={() => setWeekOffset(w => Math.max(0, w - 1))}
              disabled={weekOffset === 0}
              className="p-2 hover:bg-stone-100 rounded-lg disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>

          {/* Day Bars */}
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day, i) => {
              const log = getDayLog(day);
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className={`w-full aspect-square rounded-lg flex items-center justify-center text-xs font-bold ${
                    log?.isClean ? 'bg-brand-500 text-white' :
                    log ? 'bg-bloom-500 text-white' :
                    isToday ? 'bg-brand-100 text-brand-600 border-2 border-brand-400' :
                    'bg-stone-100 text-stone-400'
                  }`}>
                    {day.getDate()}
                  </div>
                  <span className="text-xs text-stone-500">{DAY_NAMES[day.getDay()]}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-brand-50 rounded-xl p-4 border border-brand-200">
            <p className="text-3xl font-bold text-brand-600">{cleanDays}/7</p>
            <p className="text-xs text-brand-700 mt-1">ימים נקיים השבוע</p>
          </div>
          <div className="bg-brand-50 rounded-xl p-4 border border-brand-200">
            <p className="text-3xl font-bold text-brand-600">{successRate}%</p>
            <p className="text-xs text-brand-700 mt-1">שיעור הצלחה</p>
          </div>
          <div className="bg-gold-50 rounded-xl p-4 border border-gold-200">
            <p className="text-2xl font-bold text-gold-600">{topMood ? MOOD_LABELS[topMood] : '—'}</p>
            <p className="text-xs text-gold-700 mt-1">רגש שכיח</p>
          </div>
          <div className="bg-gold-50 rounded-xl p-4 border border-gold-200">
            <p className="text-2xl font-bold text-gold-600">{userData.streak} 🔥</p>
            <p className="text-xs text-gold-700 mt-1">רצף נוכחי</p>
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white rounded-xl p-4 shadow">
          <h3 className="font-bold text-stone-800 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-brand-500" /> תובנות השבוע
          </h3>
          <ul className="space-y-2 text-sm text-stone-700">
            {cleanDays === 7 && <li className="text-brand-600 font-semibold">🏆 שבוע מושלם! 7/7 ימים נקיים!</li>}
            {cleanDays >= 5 && cleanDays < 7 && <li>✨ שבוע מצוין! {cleanDays} מתוך 7 ימים נקיים</li>}
            {cleanDays > 0 && cleanDays < 5 && <li>💪 {cleanDays} ימים נקיים - המשך להתקדם!</li>}
            {cleanDays === 0 && loggedDays > 0 && (
              <li className="text-bloom-600">🕊️ השבוע היו ימים קשים, וזה בסדר - התיעוד עצמו הוא צעד קדימה. שבוע חדש מתחיל עכשיו.</li>
            )}
            {topMood && <li>😊 הרגש השכיח ביותר השבוע: {MOOD_LABELS[topMood]}</li>}
            {topTrigger && <li>🎯 הטריגר הנפוץ: {TRIGGER_LABELS[topTrigger] || topTrigger}</li>}
            {loggedDays < 7 && <li className="text-gold-600">📝 {7 - loggedDays} ימים לא תועדו - נסה לסמן כל יום</li>}
          </ul>
        </div>

        {/* Share Button */}
        <button
          onClick={handleShare}
          className="w-full bg-brand-500 text-white py-3 rounded-xl font-bold hover:bg-brand-600 transition flex items-center justify-center gap-2"
        >
          <Download className="w-5 h-5" />
          שתף / הורד דוח
        </button>
      </div>
    </div>
  );
};

export default WeeklyReportScreen;
